import { JsonRequestEditor } from "./JsonRequestEditor";
import { hydrateRequestEditor, requireCompiledRequest } from "@/models/externalRequest";
import { collection, doc, query, setDoc, where, writeBatch } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState, type Ref } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton, SegmentedControl } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection, useDoc } from "@/hooks/firestoreHooks";
import { fetchCountryResources } from "@/models/countryResource";
import { dynamicValueDisplayText, type LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import {
  dynamicValueIsMissing,
  dynamicValueSortText,
  mappedFieldOptions,
  type MappedFieldOption,
  type RecordSortDirection,
} from "@/models/externalRecordSort";
import {
  FIELD_TYPES,
  UNIVERSAL_ATHLETE_FIELD_ROLES,
  UNIVERSAL_ATHLETE_ROLE_LABELS,
  type LeagueAthleteFieldDefinition,
  type LeagueAthleteUniversalFieldRole,
} from "@/models/fieldDefinition";
import {
  DEFAULT_SENTINEL_VALUES,
  EXTERNAL_VALUE_TRANSFORMS,
  externalDataSourceFirestoreData,
  externalSourceID,
  googleSpreadsheetGIDFromInput,
  googleSpreadsheetIDFromInput,
  parseExternalDataSource,
  type ExternalSourceKind,
  type ExternalFieldMapping,
  type ExternalValueTransform,
  type GoogleSheetAccessMode,
  type GoogleSheetOrientation,
  type LeagueExternalDataSource,
} from "@/models/externalSource";
import {
  athleteValueFirestoreData,
  fieldSchemaFirestoreData,
  parseAthleteValue,
  parseFieldSchema,
  schemaActiveFields,
  type LeagueAthleteValue,
  type LeagueFieldSchema,
} from "@/models/schema";
import { parseLeague } from "@/models/catalog";
import type { SBotUser } from "@/models/user";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { fetchExternalSourceRecords, fetchGoogleSheetTabs, type GoogleSheetTabOption } from "@/services/externalSources";
import { getGoogleSheetsAccessToken } from "@/services/googleSheetsAuth";
import { listenPersonalities, defaultWebPersonality, FALLBACK_PERSONALITY, type SBotAIPersonality } from "@/features/ai/services/aiConfigService";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import {
  deterministicProposals,
  inferExternalSourceSchema,
  mapExternalRecord,
  mappedRecordSearchText,
  newFieldFromObservation,
  prepareExternalImport,
  profileFromProposals,
  type ExternalMappingProposal,
  type ExternalSourceRecordBatch,
  type ExternalSourceSchema,
} from "./externalDataLogic";
import { aiEnhancedExternalMappingProposals } from "./externalDataAI";
import { emptyAthleteSchema } from "./athleteValues";
import "@/features/home/home.css";
import "./external-sources.css";

type ScreenMode = "list" | "wizard" | "records";
type WizardStage = "source" | "mapping" | "preview";
type MappedRecordRow = { raw: Record<string, unknown>; mapped: ReturnType<typeof mapExternalRecord> };

export interface ExternalSourcesSessionState {
  dirty: boolean;
  busy: boolean;
}

export function LeagueExternalSources() {
  const { leagueID } = useActiveLeagueID();
  if (!leagueID) return <ErrorText>Select a league workspace first.</ErrorText>;
  return <LeagueExternalSourcesContent key={leagueID} leagueID={leagueID} />;
}

/** Shared management workflow; the caller owns the league scope and presentation. */
export function LeagueExternalSourcesContent({
  leagueID,
  onSessionStateChange,
  onRequestLeave,
}: {
  leagueID: string;
  onSessionStateChange?: (state: ExternalSourcesSessionState) => void;
  onRequestLeave?: (proceed: () => void) => void;
}) {
  const { currentUser } = useAuth();
  const focusHeading = useCallback((heading: HTMLHeadingElement | null) => {
    if (onRequestLeave) heading?.focus();
  }, [onRequestLeave]);
  const headingRef = onRequestLeave ? focusHeading : undefined;
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);
  const sportID = league.data?.sportID;
  const schemaDoc = useDoc(leagueID ? doc(db, "leagueAthleteSchemas", leagueID) : null, parseFieldSchema);
  const sourcesQuery = leagueID ? query(collection(db, "leagueExternalDataSources"), where("leagueID", "==", leagueID)) : null;
  const sources = useCollection(sourcesQuery, parseExternalDataSource, `external-sources:${leagueID}`);
  const valuesQuery = leagueID ? query(collection(db, "leagueAthleteValues"), where("leagueID", "==", leagueID)) : null;
  const athleteValues = useCollection(valuesQuery, parseAthleteValue, `external-source-athletes:${leagueID}`);

  const [mode, setMode] = useState<ScreenMode>("list");
  const [editingSource, setEditingSource] = useState<LeagueExternalDataSource | null>(null);
  const [recordsSource, setRecordsSource] = useState<LeagueExternalDataSource | null>(null);
  const [recordBatch, setRecordBatch] = useState<ExternalSourceRecordBatch | null>(null);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [wizardSession, setWizardSession] = useState<ExternalSourcesSessionState>({ dirty: false, busy: false });
  const dirty = mode === "wizard" && wizardSession.dirty;
  const busy = statusBusy || isLoadingRecords || (mode === "wizard" && wizardSession.busy);
  useEffect(() => {
    onSessionStateChange?.({ dirty, busy });
  }, [dirty, busy, onSessionStateChange]);

  const changeSourceStatus = async (source: LeagueExternalDataSource, status: "active" | "archived") => {
    if (!currentUser || statusBusy) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await saveSourceStatus(source, status, currentUser.id);
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : "Source status could not be updated.");
    } finally {
      setStatusBusy(false);
    }
  };

  const [personalities, setPersonalities] = useState<SBotAIPersonality[]>([FALLBACK_PERSONALITY]);
  useEffect(() => listenPersonalities(setPersonalities), []);

  const schema = useMemo<LeagueFieldSchema | null>(() => {
    if (!leagueID) return null;
    return schemaDoc.data ?? emptyAthleteSchema(leagueID, sportID);
  }, [leagueID, schemaDoc.data, sportID]);
  const fields = schemaActiveFields(schema);
  const sortedSources = useMemo(
    () =>
      [...sources.data].sort((first, second) => {
        if (first.status !== second.status) return first.status === "active" ? -1 : 1;
        return first.name.localeCompare(second.name, undefined, { sensitivity: "base" });
      }),
    [sources.data],
  );

  const startNew = () => {
    setWizardSession({ dirty: false, busy: false });
    setEditingSource(null);
    setMode("wizard");
  };
  const editSource = (source: LeagueExternalDataSource) => {
    setWizardSession({ dirty: false, busy: false });
    setEditingSource(source);
    setMode("wizard");
  };
  const browseSource = async (source: LeagueExternalDataSource) => {
    if (!leagueID) return;
    setRecordsSource(source);
    setRecordBatch(null);
    setRecordsError(null);
    setMode("records");
    setIsLoadingRecords(true);
    try {
      const googleAccessToken = source.sourceKind === "googleSheets" && source.accessMode === "private" ? await getGoogleSheetsAccessToken() : undefined;
      setRecordBatch(await fetchExternalSourceRecords({ leagueID, sourceID: source.id, googleAccessToken }));
    } catch (error) {
      setRecordsError(error instanceof Error ? error.message : "External source records could not be loaded.");
    } finally {
      setIsLoadingRecords(false);
    }
  };

  if (!leagueID || !currentUser) return <ErrorText>Select a league workspace first.</ErrorText>;

  if (league.loading || schemaDoc.loading || athleteValues.loading) return <LoadingView label="Loading source manager..." />;
  if (!league.data) return <ErrorText>{league.error ?? "League could not be loaded."}</ErrorText>;
  if (schemaDoc.error || athleteValues.error) return <ErrorText>{schemaDoc.error ?? athleteValues.error}</ErrorText>;
  const leagueName = league.data.name;
  if (mode === "wizard") {
    return (
      <ExternalSourceWizard
        headingRef={headingRef}
        leagueID={leagueID}
        sportID={sportID}
        leagueName={leagueName}
        schema={schema}
        schemaWasStored={schemaDoc.data !== null}
        fields={fields}
        athleteValues={athleteValues.data}
        user={currentUser}
        existingSource={editingSource}
        personality={defaultWebPersonality(personalities)}
        onSessionStateChange={setWizardSession}
        onClose={() => {
          const leave = () => {
            setMode("list");
            setEditingSource(null);
          };
          if (onRequestLeave) onRequestLeave(leave);
          else leave();
        }}
      />
    );
  }
  if (mode === "records" && recordsSource) {
    return (
      <ExternalRecordsBrowser
        headingRef={headingRef}
        source={recordsSource}
        batch={recordBatch}
        loading={isLoadingRecords}
        error={recordsError}
        searchText={searchText}
        onSearchText={setSearchText}
        onBack={() => setMode("list")}
        onRefresh={() => void browseSource(recordsSource)}
      />
    );
  }

  return (
    <div className="list-screen external-sources">
      <div className="list-screen-head">
        <div>
          <h1 ref={headingRef} tabIndex={headingRef ? -1 : undefined}>External Data Sources</h1>
          <p className="home-hint">Link JSON feeds, map athlete fields, browse live source data, and import records for {leagueName}.</p>
        </div>
        <PrimaryButton title="Add Source" onClick={startNew} disabled={statusBusy} />
      </div>
      <ErrorText>{statusError ?? sources.error ?? schemaDoc.error ?? athleteValues.error}</ErrorText>
      <SBCard className="home-card">
        <div className="home-card-head">
          <div>
            <h3>Sources</h3>
            <p className="home-hint">{sortedSources.length} configured</p>
          </div>
        </div>
        {sources.loading ? (
          <LoadingView label="Loading sources..." />
        ) : sortedSources.length === 0 ? (
          <p className="home-hint">No external sources have been linked yet.</p>
        ) : (
          sortedSources.map((source) => (
            <SourceRow
              key={source.id}
              source={source}
              onBrowse={() => void browseSource(source)}
              onEdit={() => editSource(source)}
              disabled={statusBusy}
              onStatus={(status) => void changeSourceStatus(source, status)}
            />
          ))
        )}
      </SBCard>
    </div>
  );
}

function SourceRow({
  source,
  onBrowse,
  onEdit,
  onStatus,
  disabled,
}: {
  source: LeagueExternalDataSource;
  disabled: boolean;
  onBrowse: () => void;
  onEdit: () => void;
  onStatus: (status: "active" | "archived") => void;
}) {
  return (
    <div className={`external-source-row ${source.status === "archived" ? "is-archived" : ""}`}>
      <button type="button" className="external-source-main" onClick={onBrowse} disabled={disabled}>
        <strong>{source.name}</strong>
        <span>{sourceSubtitle(source)}</span>
      </button>
      <div className="external-source-actions">
        <button type="button" className="icon-button-text" onClick={onEdit} disabled={disabled}>
          Edit
        </button>
        <button type="button" className="icon-button-text" disabled={disabled} onClick={() => onStatus(source.status === "active" ? "archived" : "active")}>
          {source.status === "active" ? "Archive" : "Restore"}
        </button>
      </div>
    </div>
  );
}

function ExternalSourceWizard({
  headingRef,
  leagueID,
  sportID,
  leagueName,
  schema,
  schemaWasStored,
  fields,
  athleteValues,
  user,
  existingSource,
  personality,
  onClose,
  onSessionStateChange,
}: {
  headingRef?: Ref<HTMLHeadingElement>;
  leagueID: string;
  sportID?: string;
  leagueName: string;
  schema: LeagueFieldSchema | null;
  schemaWasStored: boolean;
  fields: LeagueAthleteFieldDefinition[];
  athleteValues: LeagueAthleteValue[];
  user: SBotUser;
  existingSource: LeagueExternalDataSource | null;
  personality: SBotAIPersonality;
  onSessionStateChange: (state: ExternalSourcesSessionState) => void;
  onClose: () => void;
}) {
  const userID = user.id;
  const [stage, setStage] = useState<WizardStage>("source");
  const [sourceKind, setSourceKind] = useState<ExternalSourceKind>(existingSource?.sourceKind ?? "json");
  const [name, setName] = useState(existingSource?.name ?? "");
  const [initialRequest] = useState(() => hydrateRequestEditor(existingSource));
  const [requestEditor, setRequestEditor] = useState(initialRequest.state);
  const endpointURL = requestEditor.url;
  const [recordsPath, setRecordsPath] = useState(existingSource?.recordsPath ?? "");
  const [pastedJSON, setPastedJSON] = useState("");
  const [spreadsheetInput, setSpreadsheetInput] = useState(existingSource?.spreadsheetURL ?? existingSource?.spreadsheetID ?? "");
  const [spreadsheetRange, setSpreadsheetRange] = useState(existingSource?.spreadsheetRange ?? "A1:Z57");
  const [sheetTabs, setSheetTabs] = useState<GoogleSheetTabOption[]>([]);
  const [selectedSpreadsheetTabID, setSelectedSpreadsheetTabID] = useState(existingSource?.spreadsheetTabID !== undefined ? String(existingSource.spreadsheetTabID) : "");
  const [selectedSpreadsheetTabName, setSelectedSpreadsheetTabName] = useState(existingSource?.spreadsheetTabName ?? "");
  const [sheetOrientation, setSheetOrientation] = useState<GoogleSheetOrientation>(existingSource?.sheetOrientation ?? "keysInFirstRow");
  const [accessMode, setAccessMode] = useState<GoogleSheetAccessMode>(existingSource?.accessMode ?? "public");
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const [recordKeyStrategy, setRecordKeyStrategy] = useState<"participant-id" | "full-name" | "random">(
    existingSource?.mappingProfile.recordKeyStrategy ?? "participant-id",
  );
  const [batch, setBatch] = useState<ExternalSourceRecordBatch | null>(null);
  const [schemaInference, setSchemaInference] = useState<ExternalSourceSchema | null>(null);
  const [proposals, setProposals] = useState<ExternalMappingProposal[]>(() =>
    existingSource?.mappingProfile.mappings.map((mapping) => ({ sourcePath: mapping.sourcePath, disposition: "map", mapping, confidence: mapping.confidence, provenance: mapping.provenance })) ?? [],
  );
  const [aiNotice, setAINotice] = useState<string | null>(null);
  const [importAsActive, setImportAsActive] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [operationBusy, setBusy] = useState(false);
  const [loadingSheetTabs, setLoadingSheetTabs] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const busy = operationBusy || loadingSheetTabs || connectingGoogle;

  const profile = useMemo(() => profileFromProposals(proposals, recordKeyStrategy), [proposals, recordKeyStrategy]);
  const previewRecords = useMemo(() => (batch ? batch.records.slice(0, 25).map((record) => ({ raw: record, mapped: mapExternalRecord(record, profile) })) : []), [batch, profile]);
  const acceptedNewFields = useMemo(() => proposals.flatMap((proposal) => (proposal.disposition === "newField" && proposal.proposedNewField ? [proposal.proposedNewField] : [])), [proposals]);

  // Include unsaved import inputs as well as persisted source configuration.
  const draftSnapshot = JSON.stringify({ sourceKind, name, requestEditor, recordsPath, pastedJSON,
    spreadsheetInput, spreadsheetRange, selectedSpreadsheetTabID, selectedSpreadsheetTabName,
    sheetOrientation, accessMode, recordKeyStrategy, proposals, importAsActive });
  const [savedSnapshot, setSavedSnapshot] = useState(draftSnapshot);
  const dirty = draftSnapshot !== savedSnapshot;
  useEffect(() => {
    onSessionStateChange({ dirty, busy });
  }, [dirty, busy, onSessionStateChange]);

  const connectGoogleSheets = async () => {
    setConnectingGoogle(true);
    try {
      const token = await getGoogleSheetsAccessToken();
      setGoogleAccessToken(token);
      setStatusMessage("Google Sheets connected for this session.");
      return token;
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "Google Sheets could not be connected.");
      throw error;
    } finally {
      setConnectingGoogle(false);
    }
  };

  const changeSourceKind = (nextSourceKind: ExternalSourceKind) => {
    setSourceKind(nextSourceKind);
    setBatch(null);
    setSchemaInference(null);
    setProposals([]);
    setAINotice(null);
    setStatusMessage(null);
  };

  const loadSpreadsheetTabs = async () => {
    setLoadingSheetTabs(true);
    setStatusMessage(null);
    try {
      const token = accessMode === "private" ? googleAccessToken ?? (await connectGoogleSheets()) : undefined;
      const response = await fetchGoogleSheetTabs({
        leagueID,
        spreadsheetURL: spreadsheetInput.trim(),
        spreadsheetID: googleSpreadsheetIDFromInput(spreadsheetInput),
        accessMode,
        googleAccessToken: token,
      });
      setSheetTabs(response.tabs);
      const gid = googleSpreadsheetGIDFromInput(spreadsheetInput);
      const selected =
        response.tabs.find((tab) => gid !== undefined && tab.sheetID === gid) ??
        response.tabs.find((tab) => selectedSpreadsheetTabID && String(tab.sheetID) === selectedSpreadsheetTabID) ??
        response.tabs.find((tab) => selectedSpreadsheetTabName && tab.title === selectedSpreadsheetTabName) ??
        response.tabs[0];
      if (selected) {
        setSelectedSpreadsheetTabID(String(selected.sheetID));
        setSelectedSpreadsheetTabName(selected.title);
      } else {
        setSelectedSpreadsheetTabID("");
        setSelectedSpreadsheetTabName("");
      }
      setStatusMessage(response.tabs.length > 0 ? "Spreadsheet tabs loaded." : "No spreadsheet tabs were returned.");
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "Spreadsheet tabs could not be loaded.");
    } finally {
      setLoadingSheetTabs(false);
    }
  };

  const selectSpreadsheetTab = (sheetID: string) => {
    setSelectedSpreadsheetTabID(sheetID);
    const tab = sheetTabs.find((candidate) => String(candidate.sheetID) === sheetID);
    setSelectedSpreadsheetTabName(tab?.title ?? "");
  };

  const fetchAndInfer = async () => {
    setBusy(true);
    setStatusMessage(null);
    setAINotice(null);
    try {
      const loaded =
        sourceKind === "googleSheets"
          ? await fetchExternalSourceRecords({
              leagueID,
              sourceKind,
              spreadsheetURL: spreadsheetInput.trim(),
              spreadsheetID: googleSpreadsheetIDFromInput(spreadsheetInput),
              spreadsheetRange: spreadsheetRange.trim(),
              spreadsheetTabName: selectedSpreadsheetTabName || undefined,
              spreadsheetTabID: selectedSpreadsheetTabID ? Number(selectedSpreadsheetTabID) : undefined,
              sheetOrientation,
              accessMode,
              googleAccessToken: accessMode === "private" ? googleAccessToken ?? (await connectGoogleSheets()) : undefined,
            })
          : await fetchExternalSourceRecords({
              leagueID,
              sourceKind,
              ...(pastedJSON.trim() ? {} : requireCompiledRequest(requestEditor)),
              recordsPath: recordsPath.trim() || undefined,
              pastedJSON: pastedJSON.trim() || undefined,
            });
      setBatch(loaded);
      if (sourceKind === "json") setRecordsPath(loaded.suggestedRecordsPath ?? recordsPath);
      const inferred = inferExternalSourceSchema(loaded);
      setSchemaInference(inferred);
      const deterministic = deterministicProposals(inferred, fields);
      setProposals(deterministic);
      setStage("mapping");
      try {
        const enhanced = await aiEnhancedExternalMappingProposals({ schema: inferred, existingFields: fields, personality, deterministic });
        setProposals(enhanced.proposals);
        setAINotice(enhanced.aiNotice ?? null);
      } catch (error) {
        setAINotice(error instanceof Error ? error.message : "AI suggestions were unavailable.");
      }
    } catch (error) {
      setStatusMessage(error instanceof Error ? `Source could not be fetched: ${error.message}` : "Source could not be fetched.");
    } finally {
      setBusy(false);
    }
  };

  const saveSource = async () => {
    setBusy(true);
    setStatusMessage(null);
    try {
      const now = new Date();
      const spreadsheetID = sourceKind === "googleSheets" ? googleSpreadsheetIDFromInput(spreadsheetInput) : undefined;
      if (sourceKind === "googleSheets" && !spreadsheetID) throw new Error("Enter a valid Google Sheet link or spreadsheet ID.");
      const compiledRequest = sourceKind === "json" ? requireCompiledRequest(requestEditor) : undefined;
      const id = existingSource?.id ?? externalSourceID(leagueID, name || endpointURL || spreadsheetID);
      const source: LeagueExternalDataSource = {
        id,
        leagueID,
        name: name.trim() || "External source",
        entityKind: "athletes",
        sourceKind,
        endpointURL: compiledRequest?.endpointURL,
        headers: compiledRequest?.headers ?? [],
        requestEditor: sourceKind === "json" ? requestEditor : undefined,
        recordsPath: sourceKind === "json" ? recordsPath.trim() || undefined : undefined,
        spreadsheetURL: sourceKind === "googleSheets" ? spreadsheetInput.trim() : undefined,
        spreadsheetID,
        spreadsheetRange: sourceKind === "googleSheets" ? spreadsheetRange.trim() : undefined,
        spreadsheetTabName: sourceKind === "googleSheets" ? selectedSpreadsheetTabName || undefined : undefined,
        spreadsheetTabID: sourceKind === "googleSheets" && selectedSpreadsheetTabID ? Number(selectedSpreadsheetTabID) : undefined,
        sheetOrientation: sourceKind === "googleSheets" ? sheetOrientation : undefined,
        accessMode: sourceKind === "googleSheets" ? accessMode : undefined,
        mappingProfile: profile,
        status: existingSource?.status ?? "active",
        lastFetchedAt: batch ? now : existingSource?.lastFetchedAt,
        lastRecordCount: batch?.records.length ?? existingSource?.lastRecordCount,
        lastFetchError: undefined,
        createdByUserID: existingSource?.createdByUserID ?? userID,
        createdAt: existingSource?.createdAt ?? now,
        updatedAt: now,
        updatedByUserID: userID,
      };
      await setDoc(doc(db, "leagueExternalDataSources", id), externalDataSourceFirestoreData(source));
      setSavedSnapshot(draftSnapshot);
      setStatusMessage("External source saved.");
    } catch (error) {
      setStatusMessage(error instanceof Error ? `Source could not be saved: ${error.message}` : "External source could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const importRecords = async () => {
    if (!schema || !batch) return;
    setBusy(true);
    setStatusMessage(null);
    try {
      const countries = await fetchCountryResources().catch(() => []);
      const prepared = prepareExternalImport({
        leagueID,
        sportID,
        records: batch.records,
        profile,
        acceptedNewFields,
        importAsActive,
        updatedByUserID: userID,
        currentSchema: schema,
        existingValues: athleteValues.flatMap((value) => (value ? [value] : [])),
        countries,
      });
      const writes = writeBatch(db);
      if (prepared.schemaChanged) {
        writes.set(doc(db, "leagueAthleteSchemas", leagueID), fieldSchemaFirestoreData(prepared.schema), { merge: true });
      }
      prepared.values.forEach((value) => {
        writes.set(doc(db, "leagueAthleteValues", value.id), athleteValueFirestoreData(value), { merge: true });
      });
      await writes.commit();
      if (prepared.schemaChanged) {
        void recordActionAudit({
          before: schemaWasStored ? fieldSchemaFirestoreData(schema) : undefined,
          after: fieldSchemaFirestoreData(prepared.schema),
          context: {
            leagueID,
            targetCollection: "leagueAthleteSchemas",
            targetDocumentID: leagueID,
            targetDisplayName: "Athlete Fields",
            actionType: schemaWasStored ? "update" : "create",
            actor: humanActionAuditActor(user),
          },
        });
      }
      setStatusMessage(
        `Import complete: ${prepared.summary.createdCount} created, ${prepared.summary.updatedCount} updated, ${prepared.summary.skippedCount} skipped.`,
      );
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="list-screen external-sources external-workflow-fieldset" disabled={busy}>
      <div className="list-screen-head">
        <div>
          <h1 ref={headingRef} tabIndex={headingRef ? -1 : undefined}>{existingSource ? "Edit External Source" : "Add External Source"}</h1>
          <p className="home-hint">{leagueName} athlete import workflow</p>
        </div>
        <SecondaryButton title="Back" onClick={onClose} disabled={busy} />
      </div>
      <SegmentedControl
        value={stage}
        onChange={setStage}
        options={[
          { value: "source", label: "Source" },
          { value: "mapping", label: "Mapping" },
          { value: "preview", label: "Preview" },
        ]}
      />
      {statusMessage && (
        <p role="status" className={statusMessage.includes("failed") || statusMessage.includes("could not") ? "sb-error-text" : "home-hint"}>
          {statusMessage}
        </p>
      )}
      {stage === "source" && (
        <SBCard className="home-card">
          <SegmentedControl
            value={sourceKind}
            onChange={changeSourceKind}
            options={[
              { value: "json", label: "JSON URL" },
              { value: "googleSheets", label: "Google Sheet" },
            ]}
          />
          <FieldLabel label="Source name">
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder={sourceKind === "googleSheets" ? "Start list sheet" : "Timing feed"} />
          </FieldLabel>
          {sourceKind === "json" ? (
            <>
              <JsonRequestEditor value={requestEditor} onChange={setRequestEditor} notice={initialRequest.notice} />
              <FieldLabel label="Records path">
                <input value={recordsPath} onChange={(event) => setRecordsPath(event.target.value)} placeholder="data.athletes" />
              </FieldLabel>
              <FieldLabel label="Pasted JSON sample">
                <textarea value={pastedJSON} onChange={(event) => setPastedJSON(event.target.value)} rows={5} placeholder="Optional local sample for inference" />
              </FieldLabel>
            </>
          ) : (
            <>
              <div className="external-sheet-link-row">
                <FieldLabel label="Spreadsheet link or ID">
                  <input
                    value={spreadsheetInput}
                    onChange={(event) => {
                      setSpreadsheetInput(event.target.value);
                      setSheetTabs([]);
                      setSelectedSpreadsheetTabID("");
                      setSelectedSpreadsheetTabName("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void loadSpreadsheetTabs();
                      }
                    }}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                  />
                </FieldLabel>
                <div className="external-sheet-load-action">
                  <SecondaryButton title={loadingSheetTabs ? "Loading..." : "Load tabs"} onClick={() => void loadSpreadsheetTabs()} disabled={busy || loadingSheetTabs} />
                </div>
              </div>
              {(sheetTabs.length > 0 || selectedSpreadsheetTabName) && (
                <FieldLabel label="Sheet tab">
                  <select value={selectedSpreadsheetTabID} onChange={(event) => selectSpreadsheetTab(event.target.value)}>
                    {selectedSpreadsheetTabName && !sheetTabs.some((tab) => tab.title === selectedSpreadsheetTabName) && (
                      <option value={selectedSpreadsheetTabID}>{selectedSpreadsheetTabName}</option>
                    )}
                    {sheetTabs.map((tab) => (
                      <option key={tab.sheetID} value={String(tab.sheetID)}>
                        {tab.title}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
              )}
              <div className="external-sheet-grid">
                <FieldLabel label="Range">
                  <input value={spreadsheetRange} onChange={(event) => setSpreadsheetRange(event.target.value)} placeholder="A1:Z57" />
                </FieldLabel>
                <FieldLabel label="Keys">
                  <select value={sheetOrientation} onChange={(event) => setSheetOrientation(event.target.value as GoogleSheetOrientation)}>
                    <option value="keysInFirstRow">First row contains keys</option>
                    <option value="keysInFirstColumn">First column contains keys</option>
                  </select>
                </FieldLabel>
                <FieldLabel label="Access">
                  <select
                    value={accessMode}
                    onChange={(event) => {
                      setAccessMode(event.target.value as GoogleSheetAccessMode);
                      setGoogleAccessToken(null);
                    }}
                  >
                    <option value="public">Public sheet</option>
                    <option value="private">Private sheet</option>
                  </select>
                </FieldLabel>
              </div>
              <p className="external-notice">
                {sheetOrientation === "keysInFirstRow"
                  ? "Row 1 is treated as field keys. Rows 2 onward become athlete records."
                  : "Column A is treated as field keys. Columns B onward become athlete records."}
              </p>
              {accessMode === "private" && (
                <div className="external-button-row">
                  <SecondaryButton title={googleAccessToken ? "Google Connected" : "Connect Google Sheets"} onClick={() => void connectGoogleSheets().catch(() => {})} disabled={busy} />
                </div>
              )}
            </>
          )}
          <div className="external-button-row">
            <PrimaryButton title={busy ? "Fetching..." : "Fetch & Infer"} onClick={() => void fetchAndInfer()} disabled={busy} />
            <SecondaryButton title="Save Source" onClick={() => void saveSource()} disabled={busy || profile.mappings.length === 0} />
          </div>
        </SBCard>
      )}
      {stage === "mapping" && (
        <SBCard className="home-card">
          <div className="home-card-head">
            <div>
              <h3>Mapping Review</h3>
              <p className="home-hint">
                {schemaInference?.fields.length ?? 0} inferred fields, {profile.mappings.length} mapped
              </p>
            </div>
            <FieldLabel label="Record key">
              <select value={recordKeyStrategy} onChange={(event) => setRecordKeyStrategy(event.target.value as typeof recordKeyStrategy)}>
                <option value="participant-id">Participant ID</option>
                <option value="full-name">Full name</option>
                <option value="random">Random</option>
              </select>
            </FieldLabel>
          </div>
          {aiNotice && <p className="external-notice">{aiNotice}</p>}
          {!schemaInference ? (
            <p className="home-hint">Fetch a source before reviewing mappings.</p>
          ) : (
            <div className="mapping-table">
              {schemaInference.fields
                .filter((field) => field.observedTypes.some((type) => ["string", "integer", "double", "bool"].includes(type)))
                .map((observation) => (
                  <MappingRow
                    key={observation.path}
                    observation={observation}
                    fields={fields}
                    proposal={proposals.find((proposal) => proposal.sourcePath === observation.path)}
                    onChange={(proposal) => setProposals((current) => upsertProposal(current, proposal))}
                  />
                ))}
            </div>
          )}
          <div className="external-button-row">
            <SecondaryButton title="Back" onClick={() => setStage("source")} disabled={busy} />
            <PrimaryButton title="Preview Import" onClick={() => setStage("preview")} disabled={!batch || profile.mappings.length === 0} />
            <SecondaryButton title="Save Source" onClick={() => void saveSource()} disabled={busy || profile.mappings.length === 0} />
          </div>
        </SBCard>
      )}
      {stage === "preview" && (
        <SBCard className="home-card">
          <div className="home-card-head">
            <div>
              <h3>Import Preview</h3>
              <p className="home-hint">{batch?.records.length ?? 0} source records</p>
            </div>
            <label className="external-check">
              <input type="checkbox" checked={importAsActive} onChange={(event) => setImportAsActive(event.target.checked)} />
              Active
            </label>
          </div>
          <PreviewRecords records={previewRecords} mappings={profile.mappings} />
          <div className="external-button-row">
            <SecondaryButton title="Back" onClick={() => setStage("mapping")} disabled={busy} />
            <SecondaryButton title="Save Source" onClick={() => void saveSource()} disabled={busy || profile.mappings.length === 0} />
            <PrimaryButton title={busy ? "Importing..." : "Import Records"} onClick={() => void importRecords()} disabled={busy || !batch || !schema} />
          </div>
        </SBCard>
      )}
    </fieldset>
  );
}

function MappingRow({
  observation,
  proposal,
  fields,
  onChange,
}: {
  observation: ExternalSourceSchema["fields"][number];
  proposal: ExternalMappingProposal | undefined;
  fields: LeagueAthleteFieldDefinition[];
  onChange: (proposal: ExternalMappingProposal) => void;
}) {
  const disposition = proposal?.disposition ?? "unmatched";
  const mapping = proposal?.mapping;
  const proposedField = proposal?.proposedNewField ?? newFieldFromObservation(observation, fields.length);
  const updateMapping = (changes: Partial<ExternalFieldMapping>) => {
    if (!proposal?.mapping) return;
    onChange({ ...proposal, mapping: { ...proposal.mapping, ...changes }, provenance: "user" });
  };
  const changeDisposition = (next: ExternalMappingProposal["disposition"]) => {
    if (next === "skip" || next === "unmatched") {
      onChange({ sourcePath: observation.path, disposition: next, confidence: 1, provenance: "user" });
      return;
    }
    if (next === "newField") {
      onChange({
        sourcePath: observation.path,
        disposition: "newField",
        proposedNewField: proposedField,
        mapping: mappingForNewField(observation.path, proposedField),
        confidence: 1,
        provenance: "user",
      });
      return;
    }
    const target = fields[0];
    if (target) {
      onChange({
        sourcePath: observation.path,
        disposition: "map",
        mapping: mappingForField(observation.path, target),
        confidence: 1,
        provenance: "user",
      });
    }
  };
  return (
    <div className="mapping-row">
      <div className="mapping-source">
        <strong>{observation.path}</strong>
        <span>{observation.sampleValues.slice(0, 3).join(" | ") || observation.observedTypes.join("/")}</span>
      </div>
      <FieldLabel label="Mapping action">
        <select value={disposition} onChange={(event) => changeDisposition(event.target.value as ExternalMappingProposal["disposition"])}>
          <option value="map">Map</option>
          <option value="newField">New field</option>
          <option value="skip">Skip</option>
          <option value="unmatched">Unmatched</option>
        </select>
      </FieldLabel>
      {disposition === "map" && (
        <>
          <FieldLabel label="Target field">
            <select
              value={mapping?.targetFieldID ?? ""}
              onChange={(event) => {
                const field = fields.find((entry) => entry.id === event.target.value);
                if (field) onChange({ sourcePath: observation.path, disposition: "map", mapping: mappingForField(observation.path, field), confidence: 1, provenance: "user" });
              }}
            >
              <option value="">Select field</option>
              {fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.label}
                </option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Universal role">
            <select
              value={mapping?.targetUniversalRole ?? ""}
              onChange={(event) => {
                const role = event.target.value as LeagueAthleteUniversalFieldRole;
                if (UNIVERSAL_ATHLETE_FIELD_ROLES.includes(role)) {
                  const field = fields.find((entry) => entry.universalAthleteRole === role);
                  onChange({
                    sourcePath: observation.path,
                    disposition: field ? "map" : "newField",
                    mapping: field ? mappingForField(observation.path, field) : mappingForRole(observation.path, role),
                    proposedNewField: field ? undefined : newFieldForRole(role, fields.length),
                    confidence: 1,
                    provenance: "user",
                  });
                }
              }}
            >
              <option value="">Universal role</option>
              {UNIVERSAL_ATHLETE_FIELD_ROLES.map((role) => (
                <option key={role} value={role}>
                  {UNIVERSAL_ATHLETE_ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </FieldLabel>
        </>
      )}
      {disposition === "newField" && (
        <>
          <FieldLabel label="Field ID">
            <input
              value={proposal?.proposedNewField?.id ?? proposedField.id}
              onChange={(event) => {
                const field = { ...proposedField, id: event.target.value };
                onChange({ sourcePath: observation.path, disposition: "newField", proposedNewField: field, mapping: mappingForNewField(observation.path, field), confidence: 1, provenance: "user" });
              }}
            />
          </FieldLabel>
          <FieldLabel label="Field label">
            <input
              value={proposal?.proposedNewField?.label ?? proposedField.label}
              onChange={(event) => {
                const field = { ...proposedField, label: event.target.value };
                onChange({ sourcePath: observation.path, disposition: "newField", proposedNewField: field, mapping: mappingForNewField(observation.path, field), confidence: 1, provenance: "user" });
              }}
            />
          </FieldLabel>
          <FieldLabel label="Field type">
            <select
              value={proposal?.proposedNewField?.type ?? proposedField.type}
              onChange={(event) => {
                const field = { ...proposedField, type: event.target.value as LeagueAthleteFieldDefinition["type"] };
                onChange({ sourcePath: observation.path, disposition: "newField", proposedNewField: field, mapping: mappingForNewField(observation.path, field), confidence: 1, provenance: "user" });
              }}
            >
              {FIELD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </FieldLabel>
        </>
      )}
      {mapping && disposition !== "skip" && disposition !== "unmatched" && (
        <div className="mapping-advanced">
          <label>
            <span>Transforms</span>
            <input
              value={mapping.transforms.join(", ")}
              onChange={(event) => updateMapping({ transforms: parseTransformCSV(event.target.value) })}
              placeholder="trim, value-map"
            />
          </label>
          <label>
            <span>Fallback paths</span>
            <input
              value={mapping.fallbackPaths.join(", ")}
              onChange={(event) => updateMapping({ fallbackPaths: commaList(event.target.value) })}
              placeholder="altName, profile.name"
            />
          </label>
          <label>
            <span>Sentinels</span>
            <input
              value={mapping.sentinelValues.join(", ")}
              onChange={(event) => updateMapping({ sentinelValues: commaList(event.target.value) })}
            />
          </label>
          <label>
            <span>Value map</span>
            <textarea
              value={valueMapToText(mapping.valueMap)}
              onChange={(event) => updateMapping({ valueMap: parseValueMapText(event.target.value) })}
              placeholder="MPRO=professional"
              rows={2}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function ExternalRecordsBrowser({
  headingRef,
  source,
  batch,
  loading,
  error,
  searchText,
  onSearchText,
  onBack,
  onRefresh,
}: {
  headingRef?: Ref<HTMLHeadingElement>;
  source: LeagueExternalDataSource;
  batch: ExternalSourceRecordBatch | null;
  loading: boolean;
  error: string | null;
  searchText: string;
  onSearchText: (value: string) => void;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const [filterFieldID, setFilterFieldID] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterMin, setFilterMin] = useState("");
  const [filterMax, setFilterMax] = useState("");
  const [sortFieldID, setSortFieldID] = useState("");
  const [sortDirection, setSortDirection] = useState<RecordSortDirection>("asc");
  useEffect(() => {
    onSearchText("");
    setFilterFieldID("");
    setFilterText("");
    setFilterMin("");
    setFilterMax("");
    setSortFieldID("");
    setSortDirection("asc");
  }, [onSearchText, source.id]);
  const mappedFields = useMemo(() => mappedFieldOptions(source.mappingProfile.mappings), [source.mappingProfile.mappings]);
  const filterField = mappedFields.find((field) => field.targetFieldID === filterFieldID);
  const filterIsNumeric = filterField ? isNumericMapping(filterField) : false;
  const rows = useMemo(() => {
    const mapped = (batch?.records ?? []).map((record) => ({ raw: record, mapped: mapExternalRecord(record, source.mappingProfile) }));
    const queryText = searchText.trim().toLowerCase();
    const searched = queryText ? mapped.filter((entry) => mappedRecordSearchText(entry.mapped).includes(queryText)) : mapped;
    const filtered = filterRows(searched, filterField, filterText, filterMin, filterMax);
    return sortRows(filtered, mappedFields.find((field) => field.targetFieldID === sortFieldID), sortDirection);
  }, [batch?.records, filterField, filterMax, filterMin, filterText, mappedFields, searchText, sortDirection, sortFieldID, source.mappingProfile]);
  const displayedRows = rows.slice(0, 100);
  const clearControls = () => {
    onSearchText("");
    setFilterFieldID("");
    setFilterText("");
    setFilterMin("");
    setFilterMax("");
    setSortFieldID("");
    setSortDirection("asc");
  };
  return (
    <div className="list-screen external-sources">
      <div className="list-screen-head">
        <div>
          <h1 ref={headingRef} tabIndex={headingRef ? -1 : undefined}>{source.name}</h1>
          <p className="home-hint">External source records are read-only until imported.</p>
        </div>
        <div className="external-button-row">
          <SecondaryButton title="Back" onClick={onBack} disabled={loading} />
          <SecondaryButton title="Refresh" onClick={onRefresh} disabled={loading} />
        </div>
      </div>
      <SBCard className="home-card">
        <div className="record-browser-controls">
          <FieldLabel label="Search records">
            <input value={searchText} onChange={(event) => onSearchText(event.target.value)} placeholder="Name, bib, country..." />
          </FieldLabel>
          <FieldLabel label="Filter field">
            <select
              value={filterFieldID}
              onChange={(event) => {
                setFilterFieldID(event.target.value);
                setFilterText("");
                setFilterMin("");
                setFilterMax("");
              }}
            >
              <option value="">Any mapped field</option>
              {mappedFields.map((field) => (
                <option key={field.targetFieldID} value={field.targetFieldID}>
                  {field.targetLabel}
                </option>
              ))}
            </select>
          </FieldLabel>
          {filterIsNumeric ? (
            <div className="record-number-filter">
              <FieldLabel label="Equals">
                <input inputMode="decimal" value={filterText} onChange={(event) => setFilterText(event.target.value)} placeholder="Any" />
              </FieldLabel>
              <FieldLabel label="Min">
                <input inputMode="decimal" value={filterMin} onChange={(event) => setFilterMin(event.target.value)} placeholder="Any" />
              </FieldLabel>
              <FieldLabel label="Max">
                <input inputMode="decimal" value={filterMax} onChange={(event) => setFilterMax(event.target.value)} placeholder="Any" />
              </FieldLabel>
            </div>
          ) : (
            <FieldLabel label="Filter value">
              <input
                value={filterText}
                onChange={(event) => setFilterText(event.target.value)}
                placeholder={filterFieldID ? "Contains..." : "Select a field first"}
                disabled={!filterFieldID}
              />
            </FieldLabel>
          )}
          <FieldLabel label="Sort field">
            <select value={sortFieldID} onChange={(event) => setSortFieldID(event.target.value)}>
              <option value="">Source order</option>
              {mappedFields.map((field) => (
                <option key={field.targetFieldID} value={field.targetFieldID}>
                  {field.targetLabel}
                </option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Direction">
            <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as RecordSortDirection)} disabled={!sortFieldID}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </FieldLabel>
          <div className="record-browser-actions">
            <SecondaryButton title="Clear" onClick={clearControls} />
          </div>
        </div>
        <p className="record-browser-count">
          Showing {displayedRows.length} of {rows.length} matching records
        </p>
        {loading ? <LoadingView label="Fetching records..." /> : <ErrorText>{error}</ErrorText>}
        <PreviewRecords records={displayedRows} mappings={source.mappingProfile.mappings} showRaw />
      </SBCard>
    </div>
  );
}

function filterRows(
  rows: MappedRecordRow[],
  field: MappedFieldOption | undefined,
  filterText: string,
  filterMin: string,
  filterMax: string,
): MappedRecordRow[] {
  if (!field) return rows;
  if (isNumericMapping(field)) {
    const exact = parseOptionalNumber(filterText);
    const min = parseOptionalNumber(filterMin);
    const max = parseOptionalNumber(filterMax);
    if (exact.invalid || min.invalid || max.invalid) return [];
    if (exact.value === undefined && min.value === undefined && max.value === undefined) return rows;
    return rows.filter((row) => {
      const value = dynamicNumericValue(row.mapped.values[field.targetFieldID]);
      if (value === undefined) return false;
      if (exact.value !== undefined && value !== exact.value) return false;
      if (min.value !== undefined && value < min.value) return false;
      if (max.value !== undefined && value > max.value) return false;
      return true;
    });
  }

  const needle = filterText.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    const value = row.mapped.values[field.targetFieldID];
    return !dynamicValueIsMissing(value) && dynamicValueFilterText(value).includes(needle);
  });
}

function sortRows(rows: MappedRecordRow[], field: MappedFieldOption | undefined, direction: RecordSortDirection): MappedRecordRow[] {
  if (!field) return rows;
  return [...rows].sort((first, second) => compareRows(first, second, field, direction));
}

function compareRows(first: MappedRecordRow, second: MappedRecordRow, field: MappedFieldOption, direction: RecordSortDirection): number {
  const firstValue = first.mapped.values[field.targetFieldID];
  const secondValue = second.mapped.values[field.targetFieldID];
  const firstMissing = dynamicValueIsMissing(firstValue);
  const secondMissing = dynamicValueIsMissing(secondValue);
  if (firstMissing || secondMissing) {
    const missingResult = firstMissing === secondMissing ? 0 : firstMissing ? 1 : -1;
    return direction === "asc" ? missingResult : -missingResult;
  }

  let result = 0;
  if (isNumericMapping(field)) {
    const firstNumber = dynamicNumericValue(firstValue);
    const secondNumber = dynamicNumericValue(secondValue);
    if (firstNumber === undefined || secondNumber === undefined) {
      const missingResult = firstNumber === secondNumber ? 0 : firstNumber === undefined ? 1 : -1;
      return direction === "asc" ? missingResult : -missingResult;
    }
    result = firstNumber - secondNumber;
  } else {
    result = dynamicValueSortText(firstValue).localeCompare(dynamicValueSortText(secondValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return direction === "asc" ? result : -result;
}

function isNumericMapping(field: MappedFieldOption): boolean {
  return field.targetType === "integer" || field.targetType === "decimal";
}

function parseOptionalNumber(text: string): { value?: number; invalid: boolean } {
  const trimmed = text.trim();
  if (!trimmed) return { invalid: false };
  const value = Number(trimmed);
  return Number.isFinite(value) ? { value, invalid: false } : { invalid: true };
}

function dynamicNumericValue(value: LeagueDynamicFieldValue | undefined): number | undefined {
  if (!value) return undefined;
  if ((value.kind === "int" || value.kind === "double") && Number.isFinite(value.value)) return value.value;
  if (value.kind === "string") {
    const parsed = Number(value.value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function dynamicValueFilterText(value: LeagueDynamicFieldValue): string {
  return dynamicValueSortText(value).toLowerCase();
}

function PreviewRecords({
  records,
  mappings,
  showRaw = false,
}: {
  records: { raw: Record<string, unknown>; mapped: ReturnType<typeof mapExternalRecord> }[];
  mappings: ExternalFieldMapping[];
  showRaw?: boolean;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(showRaw ? 0 : null);
  if (records.length === 0) return <p className="home-hint">No records to preview.</p>;
  return (
    <div className="preview-records">
      {records.map((entry, index) => {
        const isExpanded = expandedIndex === index;
        return (
          <section key={index} className={`preview-record${isExpanded ? " is-expanded" : ""}`}>
            <button
              type="button"
              className="preview-record-header"
              aria-expanded={isExpanded}
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
            >
              <span aria-hidden>{isExpanded ? "▾" : "▸"}</span>
              <strong>{recordTitle(entry.mapped, mappings)}</strong>
              {entry.mapped.issues.length > 0 && <span className="badge badge-draft">{entry.mapped.issues.length} issues</span>}
            </button>
            {isExpanded && (
              <div className="preview-record-body">
                <div className="preview-values">
                  {mappings.map((mapping) => (
                    <div key={mapping.targetFieldID} className="preview-value">
                      <span>{mapping.targetLabel}</span>
                      <strong>{dynamicValueDisplayText(entry.mapped.values[mapping.targetFieldID])}</strong>
                    </div>
                  ))}
                </div>
                {entry.mapped.issues.map((issue) => (
                  <p key={`${issue.targetFieldID}-${issue.sourcePath}`} className="sb-error-text">
                    {issue.targetFieldID}: {issue.message}
                  </p>
                ))}
                {showRaw && (
                  <div className="raw-json-viewer">
                    <span>Raw source JSON</span>
                    <pre>{JSON.stringify(entry.raw, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

async function saveSourceStatus(source: LeagueExternalDataSource, status: "active" | "archived", userID: string) {
  await setDoc(
    doc(db, "leagueExternalDataSources", source.id),
    externalDataSourceFirestoreData({ ...source, status, updatedAt: new Date(), updatedByUserID: userID }),
    { merge: true },
  );
}

function mappingForField(sourcePath: string, field: LeagueAthleteFieldDefinition): ExternalFieldMapping {
  return {
    targetFieldID: field.id,
    targetUniversalRole: field.universalAthleteRole,
    targetType: field.type,
    targetLabel: field.label,
    sourcePath,
    fallbackPaths: [],
    transforms: ["identity"],
    sentinelValues: DEFAULT_SENTINEL_VALUES,
    valueMap: [],
    confidence: 1,
    provenance: "user",
  };
}

function mappingForNewField(sourcePath: string, field: LeagueAthleteFieldDefinition): ExternalFieldMapping {
  return { ...mappingForField(sourcePath, field), targetUniversalRole: undefined };
}

function mappingForRole(sourcePath: string, role: LeagueAthleteUniversalFieldRole): ExternalFieldMapping {
  const field = newFieldForRole(role, 0);
  return { ...mappingForField(sourcePath, field), targetUniversalRole: role };
}

function newFieldForRole(role: LeagueAthleteUniversalFieldRole, sortOrder: number): LeagueAthleteFieldDefinition {
  return {
    id: role,
    label: UNIVERSAL_ATHLETE_ROLE_LABELS[role],
    type: role === "age" ? "integer" : role.startsWith("is-") ? "boolean" : role.includes("url") ? "url" : "text",
    privacyScope: "public",
    isRequired: role === "participant-id" || role === "full-name",
    options: [],
    validation: { allowedMIMETypes: [] },
    sortOrder,
    isArchived: false,
    universalAthleteRole: role,
  };
}

function upsertProposal(proposals: ExternalMappingProposal[], next: ExternalMappingProposal): ExternalMappingProposal[] {
  const index = proposals.findIndex((proposal) => proposal.sourcePath === next.sourcePath);
  if (index < 0) return [...proposals, next];
  const updated = [...proposals];
  updated[index] = next;
  return updated;
}

function commaList(text: string): string[] {
  return text
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseTransformCSV(text: string): ExternalValueTransform[] {
  return commaList(text).filter((entry): entry is ExternalValueTransform =>
    EXTERNAL_VALUE_TRANSFORMS.includes(entry as ExternalValueTransform),
  );
}

function valueMapToText(valueMap: { from: string; to: string }[]): string {
  return valueMap.map((entry) => `${entry.from}=${entry.to}`).join("\n");
}

function parseValueMapText(text: string): { from: string; to: string }[] {
  return text
    .split("\n")
    .flatMap((line) => {
      const index = line.indexOf("=");
      if (index < 0) return [];
      const from = line.slice(0, index).trim();
      const to = line.slice(index + 1).trim();
      return from && to ? [{ from, to }] : [];
    });
}

function sourceSubtitle(source: LeagueExternalDataSource): string {
  const parts: string[] = [];
  if (source.sourceKind === "googleSheets") {
    parts.push("Google Sheet");
    if (source.spreadsheetTabName) parts.push(source.spreadsheetTabName);
    if (source.spreadsheetRange) parts.push(source.spreadsheetRange);
    if (source.accessMode) parts.push(source.accessMode === "private" ? "private access" : "public access");
  } else if (source.endpointURL) {
    try {
      parts.push(new URL(source.endpointURL).host);
    } catch {
      parts.push(source.endpointURL);
    }
  }
  parts.push(`${source.mappingProfile.mappings.length} mapped fields`);
  if (source.lastRecordCount !== undefined) parts.push(`${source.lastRecordCount} records`);
  if (source.lastFetchError) parts.push(`Last fetch failed: ${source.lastFetchError}`);
  return parts.join(" | ");
}

function recordTitle(mapped: ReturnType<typeof mapExternalRecord>, mappings: ExternalFieldMapping[]): string {
  const fullName = valueForRole(mapped, mappings, "full-name");
  if (fullName) return fullName;
  const joined = [valueForRole(mapped, mappings, "first-name"), valueForRole(mapped, mappings, "last-name")].filter(Boolean).join(" ");
  return joined || valueForRole(mapped, mappings, "participant-id") || "Record";
}

function valueForRole(mapped: ReturnType<typeof mapExternalRecord>, mappings: ExternalFieldMapping[], role: LeagueAthleteUniversalFieldRole): string | undefined {
  const mapping = mappings.find((entry) => entry.targetUniversalRole === role);
  if (!mapping) return undefined;
  const value = mapped.values[mapping.targetFieldID];
  const text = dynamicValueDisplayText(value);
  return text === "—" ? undefined : text;
}
